/**
 * Component for Titles
 *
 * @module ui/modules/Titles
 * @memberof -Common
 */
import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { TitlesDefault as PalTitlesDefault } from 'web-palette/dist/components/TitlesDefault/TitlesDefault';
import { TitlesDefaultCompact as PalTitlesDefaultCompact } from 'web-palette/dist/components/TitlesDefaultCompact/TitlesDefaultCompact';
import { TitlesHeadline as PalTitlesHeadline } from 'web-palette/dist/components/TitlesHeadline/TitlesHeadline';
import { TitlesPage as PalTitlesPage } from 'web-palette/dist/components/TitlesPage/TitlesPage';
import { TitlesSection as PalTitlesSection } from 'web-palette/dist/components/TitlesSection/TitlesSection';

import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

/**
 * Represents a Titles component
 *
 * @method
 * @param { TitlesProps} props - React properties passed from composition
 * @returns Titles
 */
export const Titles = function( props ){
  const titlesRef = useRef( null );
  const { breakpoint } = useDeviceInflection();
  const {
    backgroundColor,
    countLabel,
    headline,
    headlineTag = defaultProps.headlineTag,
    linkAction,
    subheadline,
    textAlignment,
    textColor,
    variant = defaultProps.variant,
    description,
    iconName,
    image,
    size
  } = props;

  /** Getting each Titles variant to set which one to display in the return */
  const isTitlesDefault = variant === 'default' && size !== 'compact';
  const isTitlesHeadline = variant === 'headline';
  const isTitlesPage = variant === 'page';
  const isTitlesDefaultCompact = variant === 'default' && size === 'compact';
  const isTitlesSection = variant === 'section';

  /** Objects for external media and actions coming from Amplience */
  let formattedAction;
  let formattedPageAction;
  let formattedMedia;

  /** Breakpoint settings for incoming media */
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'LG' || breakpoint.CURRENT_BREAKPOINT === 'XL' );
  const isTablet = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'MD' );

  const isKnockoutLink = textColor === 'content-knockout';

  /** Formatters for incoming media and actions */
  if( image ){
    formattedMedia = getUltaAssetHtml( { image: image, mobileImage: image, tabletImage: image, isMobile: isMobile, isDesktop: isDesktop, isTablet: isTablet } );
  }

  if( linkAction ){
    formattedAction = getLinkHtml( { action: linkAction, isKnockoutLink } );
  }

  if( isTitlesPage && linkAction ){
    formattedPageAction = getLinkHtml( { action: linkAction, iconName: 'ArrowDown', iconPosition: 'after', isKnockoutLink } );
  }

  useIntersectionProcessor( {
    ...props,
    componentRef: titlesRef
  } );

  return (
    <div className='Titles'
      ref={ titlesRef }
    >
      { isTitlesDefault &&
        <PalTitlesDefault
          externalAction={ formattedAction }
          backgroundColor={ backgroundColor }
          countLabel={ countLabel }
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          textAlignment={ textAlignment }
          textColor={ textColor }
          description={ description }
        />
      }
      { isTitlesHeadline &&
        <PalTitlesHeadline
          backgroundColor={ backgroundColor }
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          textAlignment={ textAlignment }
        />
      }
      { isTitlesPage &&
        <PalTitlesPage
          externalAction={ formattedPageAction }
          headline={ headline }
          headlineTag={ headlineTag }
          linkAction={ linkAction }
          subheadline={ subheadline }
        />
      }
      { isTitlesSection &&
        <PalTitlesSection
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          description={ description }
          textAlignment={ textAlignment }
          iconName={ iconName }
          externalMediaElement={ formattedMedia }
          size={ size }
        />
      }
      { isTitlesDefaultCompact &&
        <PalTitlesDefaultCompact
          externalAction={ formattedAction }
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          linkAction={ linkAction }
          textAlignment={ textAlignment }
          textColor={ textColor }
          description={ description }
        />
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef Titles
 * @type {object}
 * @property {string} backgroundColor - background color of the Titles body content
 * @property {string} headline - title text of the Titles body
 * @property {string} headlineTag - headline tag of the Titles body
 * @property {object} linkAction - linkAction
 * @property {string} subheadline - Titles subheadline
 * @property {string} textAlignment - Titles textAlignment
 * @property {string} textColor - sets the text color of the Titles (default and compact variants only)
 * @property {string} variant - sets the variant of the Titles
 * @property {string} description - sets the description of the Titles
 * @property {string} iconName - sets the iconName of the Titles
 * @property {object} image - sets the image of the Titles
 */

export const propTypes = {
  /**
   * background color of the Titles body content
   */
  backgroundColor: PropTypes.string,
  /**
   * text/description of the Titles countLabel
   */
  countLabel: PropTypes.string,
  /**
   * title text of the Titles body
   */
  headline: PropTypes.string,
  /**
   * headline tag of the Titles body
   */
  headlineTag: PropTypes.string,
  /**
   * linkAction
   */
  linkAction: PropTypes.object,
  /**
   * titles subheadline
   */
  subheadline: PropTypes.string,
  /**
   * sets the position of the text
   */
  textAlignment: PropTypes.string,
  /**
   * sets the text color of the Titles (default and compact variants only)
   */
  textColor: PropTypes.oneOf( ['content-default', 'content-knockout'] ),
  /**
   * sets the variant of the Titles
   */
  variant: PropTypes.string,
  /**
   * sets the description of the Titles
   */
  description: PropTypes.string,
  /**
   * sets the size of the Titles
   */
  size: PropTypes.string,
  /**
   * icon name of the Titles section
   */
  iconName: PropTypes.string,
  /**
   * image component for the desktop view
   */
  image: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} headlineTag - headlineTag: 'h2' - sets the default for the headline tag
 */
export const defaultProps =  {
  headlineTag: 'h2',
  variant: 'default'
};

Titles.propTypes = propTypes;
Titles.defaultProps = defaultProps;

export default Titles;